import { ReactNode, useEffect, useMemo } from 'react';

import { useOpenModal, useUserSettings } from '@/hooks';

import {
  CurrentLayoutIndexModal,
  GlobalModalsContext,
  ShortcutsDialog,
  type GlobalModalsValue,
} from './globalModalsProvider/';

type GlobalModalsProviderProps = {
  children: ReactNode;
};

export const GlobalModalsProvider = ({ children }: GlobalModalsProviderProps) => {
  const shortcutsDialogModalController = useOpenModal();
  const currentLayoutIndexMapModalController = useOpenModal();
  const { enablePreviewContent } = useUserSettings();

  const shortcutsModal = useMemo(
    () => <ShortcutsDialog modalController={shortcutsDialogModalController}></ShortcutsDialog>,
    [shortcutsDialogModalController]
  );

  const currentLayoutIndexMapModal = useMemo(
    () => (
      <CurrentLayoutIndexModal
        modalController={currentLayoutIndexMapModalController}
      ></CurrentLayoutIndexModal>
    ),
    [currentLayoutIndexMapModalController]
  );

  /** Replace with provider state when the shortcuts modal is wired here. */
  const { handleOpenModal: handleOpenGlobalShortcutsModal } = shortcutsDialogModalController;
  const handleCurrentLayoutIndexMapModal = useMemo(
    () => (enablePreviewContent ? currentLayoutIndexMapModalController.handleOpenModal : undefined),
    [currentLayoutIndexMapModalController, enablePreviewContent]
  );

  const mountLayoutIndexModal = enablePreviewContent || currentLayoutIndexMapModalController.open;

  useEffect(() => {
    if (!enablePreviewContent && currentLayoutIndexMapModalController.open) {
      currentLayoutIndexMapModalController.onClose();
    }
  }, [enablePreviewContent, currentLayoutIndexMapModalController]);

  const value = useMemo<GlobalModalsValue>(
    () => ({
      showGlobalShortcutsModal: handleOpenGlobalShortcutsModal,
      showCurrentLayoutIndexMap: handleCurrentLayoutIndexMapModal,
    }),
    [handleOpenGlobalShortcutsModal, handleCurrentLayoutIndexMapModal]
  );

  return (
    <GlobalModalsContext.Provider value={value}>
      {children}
      {shortcutsModal}
      {mountLayoutIndexModal && currentLayoutIndexMapModal}
    </GlobalModalsContext.Provider>
  );
};
